import { createSlice } from '@reduxjs/toolkit';

import {
  METER_PROFILE_CLS,
  METER_TYPE_ERROR_CODES,
  PARTNERS
} from 'config/app-config';

import { getSessionStoragePartner } from 'utils/getSessionStoragePartner';

import type { RootState } from 'state/store';
import type { TError } from 'api/client';
import type { TMeter } from 'api/models/TMeter';

export type MeterTypeState = {
  isLoading: boolean;
  errors: TError[] | null;
  data: TMeter[];
};

export const initialState: MeterTypeState = {
  isLoading: false,
  errors: null,
  data: []
};

export const slice = createSlice({
  name: 'meterType',
  initialState,
  reducers: {
    setInitialState: (state) => {
      Object.entries(initialState).forEach(
        ([key, value]) => (state[key] = value)
      );
    },
    setIsLoading: (state) => {
      state.isLoading = true;
    },
    setSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.data = payload.meterpoints || initialState.data;
      state.errors = payload.errors;
    },
    setError: (state, { payload }) => {
      state.isLoading = false;
      state.errors = payload;
      state.data = initialState.data;
    }
  }
});

export const {
  setInitialState,
  setIsLoading,
  setError,
  setSuccess
} = slice.actions;

export class SliceSelectors {
  sliceName: string;

  constructor(sliceName: string) {
    this.sliceName = sliceName;
  }
  selectSlice = (state: RootState) => state[this.sliceName];

  selectData = (state: RootState) => this.selectSlice(state).data;

  selectIsBGECustomer = (state: RootState) => {
    const { errors } = this.selectSlice(state);
    const { MeterCurrentSupplierNEPException } = METER_TYPE_ERROR_CODES;

    return (errors || []).some(
      (error) => error.code === MeterCurrentSupplierNEPException
    );
  };

  selectIsBGCustomer = (state: RootState) => {
    const { errors } = this.selectSlice(state);
    const { MeterCurrentSupplierBGREException } = METER_TYPE_ERROR_CODES;

    return (errors || []).some(
      (error) => error.code === MeterCurrentSupplierBGREException
    );
  };

  selectIsRelatedElectricityMeter = (state: RootState) => {
    //Gas meter cannot have related meter exception
    const errors = this.selectErrors(state) || [];
    const { MeterRelatedException } = METER_TYPE_ERROR_CODES;
    const relatedMeterError = errors.filter(
      ({ code }) => code === MeterRelatedException
    );
    return relatedMeterError.length !== 0;
  };

  selectSingleMeterpoint = (state: RootState, fuel = '') => {
    const meterpoints = this.selectData(state);

    const fuelMeterpoints = meterpoints?.filter(
      (meterpoint) => meterpoint.fuelType.toLowerCase() === fuel.toLowerCase()
    );

    return fuelMeterpoints?.length > 1 ? undefined : fuelMeterpoints?.[0];
  };

  selectElectricityMeter = (state: RootState) => {
    return this.selectSingleMeterpoint(state, 'electricity');
  };

  selectGasMeter = (state: RootState) => {
    return this.selectSingleMeterpoint(state, 'gas');
  };

  selectHasE7Meter = (state: RootState) => {
    const electricityMeter = this.selectElectricityMeter(state);

    return electricityMeter?.profileClass === METER_PROFILE_CLS.DOMESTIC_E7;
  };

  selectHasElectricityTOUMeter = (state: RootState) => {
    const electricityMeter = this.selectElectricityMeter(state);

    return electricityMeter?.isTOUEligible;
  };

  selectHasElectricityPrepayMeter = (state: RootState) => {
    const electricityMeter = this.selectElectricityMeter(state);

    return electricityMeter?.isPrepay;
  };

  selectHasGasPrepayMeter = (state: RootState) => {
    const gasMeter = this.selectGasMeter(state);

    return gasMeter?.isPrepay;
  };

  selectHasGasTOUMeter = (state: RootState) => {
    const gasMeter = this.selectGasMeter(state);

    return gasMeter?.isTOUEligible;
  };

  selectIsLoading = (state: RootState) => this.selectSlice(state).isLoading;

  selectErrors = (state: RootState) => {
    const { errors } = this.selectSlice(state);
    const partner = getSessionStoragePartner();
    const { MeterCurrentSupplierBGREException } = METER_TYPE_ERROR_CODES;
    /**
     * Filter out MeterCurrentSupplierBGREException (BG Customers)
     * for voicesales home move workaround journey
     * The error should still be kept in redux state for debugging
     */
    if (partner === PARTNERS.VOICESALES_HOMEMOVE) {
      return (errors || []).filter(
        ({ code }) => code !== MeterCurrentSupplierBGREException
      );
    }
    return errors;
  };

  selectAPIErrors = (state: RootState) => {
    const errors = this.selectErrors(state) || [];

    return errors.filter((error) => error && error.keyword === '');
  };

  selectGasErrors = (state: RootState) => {
    const errors = this.selectErrors(state) || [];

    return errors.filter(
      (error) => error && error.keyword.toLowerCase() === 'gas'
    );
  };

  selectElectricityErrors = (state: RootState) => {
    const errors = this.selectErrors(state) || [];

    return errors.filter(
      (error) => error && error.keyword.toLowerCase() === 'electricity'
    );
  };
}

export const selectors = new SliceSelectors(slice.name);

export default slice.reducer;
